import { createApi, fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { authActions } from '../store/slices/auth';
import { getApiBaseUrl, VAULTISCAN_REQUEST_SOURCE } from './requestUtils';

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    // Get token from auth state
    const token = (getState() as any)?.auth?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Add customer_id to headers if available
    const auth = (getState() as { auth: { account?: any } }).auth;
    const customerId = auth.account?.user?.customer_id || auth.account?.customer_id;
    if (customerId) {
      headers.set('Customer-Id', String(customerId));
    }

    headers.set('Request-Source', VAULTISCAN_REQUEST_SOURCE);

    return headers;
  },
});

const baseQueryWithLogout: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  if (!navigator.onLine) {
    return {
      error: {
        status: 0,
        data: {
          message: 'You are currently offline. Please connect to the internet and try again.',
        },
      },
    };
  }
  const result = await baseQuery(args, api, extraOptions);
  if (result?.error?.status === 401) {
    api.dispatch(authActions.logout());
  }
  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithLogout,
  tagTypes: [
    'Client',
    'Clients',
    'Customer',
    'Customers',
    'Integration',
    'Integrations',
    'Categories',
    'Category',
    'Documents',
    'User',
    'Users',
    'CategoriesClients',
    'CategoryClients',
    'CategoriesClientsByCustomerId',
    'Groups',
    'Group',
    'Configurations',
    'Configuration',
    'CategoriesClientsGroups',
    'CategoriesClientsGroupsByCustomer',
    'GroupsByCategory',
    'SelectedGroupsByCategory',
    'Translation',
    'ChatHistory',
    'ChatHistories',
    'UploadedFiles',
    'Speech',
    'Config',
    'llmConfig',
    'llmProvider',
    'getLlmSubModels',
    'ConfigurationJson',
    'Prompts',
    'Prompt',
    'TokenUsage',
    'TokenLimit',
    'Agents',
    'Agent',
    'AgentConfigurations',
    'Connectors',
    'ConnectorTokens',
    'ConnectorConfigurations',
    'Lakehouse',
    'DataPlatformDashboard',
    'DataFile',
    'DataTable',
    'DataTablePreview',
    'DataAgent', // Add singular tag
    'NotificationTemplates',
    'Notifications',
    'NotificationCount',
    'NotificationPreferences',
    'DataConnections',
    'DataConnection',
    'SystemPrompt',
    'DataAgentSource',
    'DataAgentExampleQuery',
    'AvailableTables',
    'ConnectionTables',
    'SavedQuery',
    'CustomFieldValue',
    'CustomFieldOption',
    'CustomField',
    'VaultilakeChatHistory',
    'VaultilakeChatHistories',
    'EntityType',
    'KGNode',
    'KGEdge',
    'KGGraph',
    'EntityOption',
    'UserIntake',
    'AgentState',
    'UserDetails',
    'CrawlJob',
  ],
  endpoints: (builder) => ({}),
});
